import React, {memo, useEffect, useId, useMemo, useRef, useState} from "react";

const SDK_LOADERS = new Map();
const MAP_MARKER_ID = "selected-location-marker";
const MAP_MARKER_PREFIX = "history-marker-";
const GPS_MARKER_PREFIX = "gps-marker-";
const MAPS_ASSET_ORIGIN = "https://maps.dev.atls.net";
const PLACEHOLDER_MAPS_SDK_API_KEY = "tr-746eb753-95fb-45c1-89e3-47aef275434c";
const PLACEHOLDER_BASE_URL = "https://training.atls.net";
const PLACEHOLDER_PARTNER_MANAGED_CUSTOMER_ID = null;
const MARKER_ICON_URL = "https://psap.eli-technology.ca/static/img/eml-marker.png";
const MARKER_ICON = {
    url: MARKER_ICON_URL,
    width: 54,
    height: 54
};
const GPS_MARKER_ICON = {
    url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
    width: 32,
    height: 32
};

const EmbeddedEliMap = ({initMapData, mapData, mapMarkers = [], gpsMapMarkers = [], mapInitKey}) => {
    const reactId = useId();
    const mapDivId = useMemo(() => `eli-map-${reactId.replace(/:/g, "")}`, [reactId]);
    const stateRef = useRef({ready: false});
    const latestMarkerDataRef = useRef(mapData);
    const latestMapMarkersRef = useRef(mapMarkers);
    const latestGpsMapMarkersRef = useRef(gpsMapMarkers);
    const [mapState, setMapState] = useState({status: "idle", message: ""});

    useEffect(() => {
        latestMarkerDataRef.current = mapData;
    }, [mapData]);

    useEffect(() => {
        latestMapMarkersRef.current = mapMarkers;
    }, [mapMarkers]);

    useEffect(() => {
        latestGpsMapMarkersRef.current = gpsMapMarkers;
    }, [gpsMapMarkers]);

    useEffect(() => {
        let cancelled = false;

        stateRef.current = {ready: false};

        if (!mapInitKey || !initMapData) {
            setMapState({status: "empty", message: "Missing customer id, cannot initialize maps"});
            return undefined;
        }

        const initializeMap = async () => {
            setMapState({status: "loading", message: ""});

            try {
                await loadMapsSdk(MAPS_ASSET_ORIGIN);
                if (cancelled || !window.eliMaps?.initialize) {
                    return;
                }

                const eventHandler = (eventType) => {
                    if (eventType === "StopFloorPlansRender") {
                        stateRef.current.ready = true;
                        setTimeout(() => {
                            renderMarkers({
                                primaryMarker: latestMarkerDataRef.current || initMapData,
                                resolvedMarkers: latestMapMarkersRef.current,
                                gpsMarkers: latestGpsMapMarkersRef.current
                            });
                        }, 500);
                    }
                };

                window.eliMaps.setSelectFirstAvailableFloorOnLoad?.(false);
                window.eliMaps.initialize({
                    mapDivId,
                    mapsSdkApiKey: PLACEHOLDER_MAPS_SDK_API_KEY,
                    eliCustomerId: initMapData.eliCustomerId,
                    partnerManagedCustomerId: PLACEHOLDER_PARTNER_MANAGED_CUSTOMER_ID,
                    baseURL: PLACEHOLDER_BASE_URL,
                    eventHandler,
                    logger: () => undefined,
                    errorHandler: (error) => {
                        if (!cancelled) {
                            setMapState({
                                status: "error",
                                message: error?.message || "There was some problem while trying to load the embedded map."
                            });
                        }
                    }
                });
                window.eliMaps.setDisableClusteringAtZoom?.(0);

                setMapState({status: "ready", message: ""});
            } catch (error) {
                if (!cancelled) {
                    setMapState({
                        status: "error",
                        message: error?.message || "There was some problem while trying to load the embedded map."
                    });
                }
            }
        };

        initializeMap();

        return () => {
            cancelled = true;
            stateRef.current.ready = false;
            clearMarkers();
        };
    }, [initMapData, mapDivId, mapInitKey]);

    useEffect(() => {
        if (!stateRef.current.ready || !window.eliMaps) {
            return;
        }

        renderMarkers({
            primaryMarker: mapData,
            resolvedMarkers: mapMarkers,
            gpsMarkers: gpsMapMarkers
        });
    }, [gpsMapMarkers, mapData, mapMarkers]);

    return <div className="relative h-full w-full bg-white">
        <div id={mapDivId} className="h-full w-full"/>
        {mapState.status === "loading" && <div className="absolute inset-0 flex items-center justify-center bg-white/80 text-slate-600">
            Loading embedded map...
        </div>}
        {(mapState.status === "error" || mapState.status === "empty") && <div
            className="absolute inset-0 flex items-center justify-center px-6 text-center text-slate-600 bg-white">
            {mapState.message}
        </div>}
    </div>;
};

export default memo(EmbeddedEliMap);

function applyMarker(mapData) {
    if (!mapData || !window.eliMaps) {
        return;
    }

    clearMarkers();
    window.eliMaps.setSelectedFloor?.("");
    if (hasFloorValue(mapData.floor)) {
        window.eliMaps.setSelectedFloor?.(mapData.floor);
    }
    window.eliMaps.addMarker?.(
        MAP_MARKER_ID,
        mapData.latitude,
        mapData.longitude,
        mapData.floor,
        mapData.label,
        mapData.icon || MARKER_ICON,
        false
    );
}

function applyMarkers(mapMarkers, icon = MARKER_ICON, prefix = MAP_MARKER_PREFIX) {
    if (!mapMarkers?.length || !window.eliMaps) {
        return;
    }

    mapMarkers.forEach((marker, index) => {
        window.eliMaps.addMarker?.(
            marker.markerId || `${prefix}${index}`,
            marker.latitude,
            marker.longitude,
            marker.floor,
            marker.label,
            marker.icon || icon,
            false
        );
    });
}

function renderMarkers({primaryMarker, resolvedMarkers = [], gpsMarkers = []}) {
    if (!window.eliMaps) {
        return;
    }

    clearMarkers();

    const visibleResolvedMarkers = resolvedMarkers.length > 0 ? resolvedMarkers : [primaryMarker].filter(Boolean);
    const floorSource = visibleResolvedMarkers.find((marker) => hasFloorValue(marker?.floor))
        || gpsMarkers.find((marker) => hasFloorValue(marker?.floor));

    window.eliMaps.setSelectedFloor?.("");
    if (hasFloorValue(floorSource?.floor)) {
        window.eliMaps.setSelectedFloor?.(floorSource.floor);
    }

    if (resolvedMarkers.length > 0) {
        applyMarkers(resolvedMarkers, MARKER_ICON, MAP_MARKER_PREFIX);
    } else if (primaryMarker) {
        applyMarker(primaryMarker);
    }

    if (gpsMarkers.length > 0) {
        applyMarkers(gpsMarkers, GPS_MARKER_ICON, GPS_MARKER_PREFIX);
    }
}

function clearMarkers() {
    if (!window.eliMaps) {
        return;
    }

    window.eliMaps.removeMarker?.(MAP_MARKER_ID);
    for (let index = 0; index < 500; index += 1) {
        window.eliMaps.removeMarker?.(`${MAP_MARKER_PREFIX}${index}`);
        window.eliMaps.removeMarker?.(`${GPS_MARKER_PREFIX}${index}`);
    }
}

function hasFloorValue(floor) {
    return floor !== null && floor !== undefined && floor !== "";
}

function loadMapsSdk(assetOrigin) {
    if (window.eliMaps?.initialize) {
        return Promise.resolve();
    }

    const existingLoader = SDK_LOADERS.get(assetOrigin);
    if (existingLoader) {
        return existingLoader;
    }

    const loader = new Promise((resolve, reject) => {
        const scriptId = `eli-maps-sdk-${assetOrigin.replace(/[^a-zA-Z0-9]/g, "-")}`;

        if (!document.querySelector(`link[data-eli-maps-origin="${assetOrigin}"]`)) {
            const stylesheet = document.createElement("link");
            stylesheet.rel = "stylesheet";
            stylesheet.href = `${assetOrigin}/static/css/main.latest.css`;
            stylesheet.setAttribute("data-eli-maps-origin", assetOrigin);
            document.head.appendChild(stylesheet);
        }

        const existingScript = document.getElementById(scriptId);
        if (existingScript) {
            existingScript.addEventListener("load", resolve, {once: true});
            existingScript.addEventListener("error", () => reject(new Error("The embedded maps SDK failed to load.")), {once: true});
            return;
        }

        const script = document.createElement("script");
        script.id = scriptId;
        script.src = `${assetOrigin}/static/js/main.latest.js`;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("The embedded maps SDK failed to load."));
        document.body.appendChild(script);
    });

    SDK_LOADERS.set(assetOrigin, loader);
    return loader;
}
